import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import DOMPurify from 'dompurify';

export type ChatMessageStatus = 'sending' | 'sent' | 'failed' | 'delivered' | 'undelivered' | undefined;

@Component({
  selector: 'glxy-chat-message',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule],
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent implements AfterViewInit {
  @HostBinding('class')
  get class() {
    return ['glxy-chat-message', this.messageStatusClass];
  }

  @Input() itemContent: 'message' | 'attachment' = 'message';

  @Input() isTyping = false;

  @Input() messageText?: string;

  @Input() messageStatus?: ChatMessageStatus;

  @Input() messageTime?: string | null;

  get messageStatusText(): string {
    switch (this.messageStatus) {
      case 'delivered':
        return 'GALAXY.CHAT.MESSAGE_STATUS.DELIVERED';
      case 'failed':
      case 'undelivered':
        return 'GALAXY.CHAT.MESSAGE_STATUS.UNDELIVERED';
      case 'sent':
        return 'GALAXY.CHAT.MESSAGE_STATUS.SENT';
      case 'sending':
      default:
        return '';
    }
  }

  get messageStatusClass(): string {
    switch (this.messageStatus) {
      case 'delivered':
        return 'delivered';
      case 'failed':
      case 'undelivered':
        return 'failed';
      case 'sent':
        return 'sent';
      case 'sending':
        return 'sending';
      default:
        return '';
    }
  }

  isMetadataPresented = false;

  private eRef = inject(ElementRef);

  @ViewChild('bubbleElement') bubbleElement!: ElementRef;

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    // Check if there is any selected text
    const selectedText = window.getSelection()?.toString();
    if (selectedText) {
      return;
    }

    // Check if metadata is being clicked
    if (this.eRef.nativeElement.querySelector('.metadata')?.contains(event.target)) {
      return;
    }

    // Check if the target is inside the element and not a link
    const isLink = event.target instanceof HTMLAnchorElement;
    const isInElement = this.eRef.nativeElement.contains(event.target);
    if (!isLink && isInElement) {
      this.isMetadataPresented = !this.isMetadataPresented;
    } else {
      if (this.itemContent === 'message') {
        this.isMetadataPresented = false;
      }
    }
  }

  ngAfterViewInit() {
    if (this.messageText && !this.isTyping) {
      this.bubbleElement.nativeElement.innerHTML = DOMPurify.sanitize(this.messageText);
    }
  }
}
